import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Download, Wrench, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono ${
        ok ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-red-500/15 text-red-400 border border-red-500/30'
      }`}
    >
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {label}
    </span>
  );
}

export default function AdminDiagnostics() {
  const [loading, setLoading] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [status, setStatus] = useState(null);
  const [installResult, setInstallResult] = useState(null);

  const token = () => localStorage.getItem('token') || sessionStorage.getItem('token');

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/carousel/admin/playwright-status`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setStatus(data);
    } catch (err) {
      toast.error('取狀態失敗：' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const forceInstall = async () => {
    if (!window.confirm('強制重新安裝 Chromium？呢個會佔用 ~150MB 磁碟，需時 30-90 秒。')) return;
    setInstalling(true);
    setInstallResult(null);
    try {
      const { data } = await axios.post(
        `${API}/api/carousel/admin/playwright-install`,
        {},
        { headers: { Authorization: `Bearer ${token()}` }, timeout: 240000 },
      );
      setInstallResult(data);
      if (data.ok) {
        toast.success('Chromium 安裝成功！');
      } else {
        toast.error('安裝失敗，請睇下面 log');
      }
      // refresh status
      await fetchStatus();
    } catch (err) {
      toast.error('安裝請求失敗：' + (err.response?.data?.detail || err.message));
      setInstallResult({ ok: false, error: err.message });
    } finally {
      setInstalling(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const chromiumOk = !!status?.chromium_executable;

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto" data-testid="admin-diagnostics-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-atelier-mute mb-1">System · Diagnostics</p>
          <h1 className="text-3xl font-serif text-atelier-ink">
            系統<em className="text-atelier-coral not-italic">診斷</em>
          </h1>
          <p className="text-sm text-atelier-mute mt-1">
            Chromium 渲染服務狀態 · 輪播 PNG / ZIP 下載必需
          </p>
        </div>
        <button
          onClick={fetchStatus}
          disabled={loading}
          className="atelier-btn-ghost flex items-center gap-2"
          data-testid="diagnostics-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          重新整理
        </button>
      </div>

      {/* Chromium status card */}
      <div className="atelier-card p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="atelier-caption mb-2">CHROMIUM RUNTIME</p>
            <div className="flex items-center gap-3">
              {chromiumOk ? (
                <StatusBadge ok={true} label="READY" />
              ) : (
                <StatusBadge ok={false} label="MISSING" />
              )}
              {status?.last_install_error && (
                <span className="text-xs text-amber-500 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  上次安裝失敗
                </span>
              )}
            </div>
            {chromiumOk && (
              <p className="text-xs font-mono text-atelier-mute mt-2 break-all">
                {status.chromium_executable}
              </p>
            )}
          </div>
          <button
            onClick={forceInstall}
            disabled={installing}
            className="atelier-btn-primary flex items-center gap-2"
            data-testid="diagnostics-force-install-btn"
          >
            {installing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                安裝中…（最長 4 分鐘）
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                {chromiumOk ? '重新安裝' : '立即安裝 Chromium'}
              </>
            )}
          </button>
        </div>

        {!chromiumOk && (
          <div className="border-l-2 border-atelier-coral pl-4 py-2 bg-atelier-coral/5 text-sm">
            <p className="text-atelier-ink mb-1 font-medium">⚠️ 輪播下載功能未就緒</p>
            <p className="text-atelier-mute text-xs leading-relaxed">
              Production pod 嘅 Playwright Chromium 未安裝。click「立即安裝 Chromium」按鈕，
              系統會自動 trigger <code className="px-1 bg-atelier-surface rounded text-atelier-coral">python -m playwright install chromium</code>。
              如果失敗，下面會顯示完整 stderr 畀你 / 技術支援判斷。
            </p>
          </div>
        )}
      </div>

      {/* Install paths */}
      {status && (
        <div className="atelier-card p-6 space-y-3">
          <p className="atelier-caption">CANDIDATE BROWSER PATHS</p>
          <div className="space-y-2 font-mono text-xs">
            {status.candidate_paths.map((p) => (
              <div key={p.path} className="flex items-center justify-between p-3 bg-atelier-surface-2 rounded border border-atelier-line">
                <div className="flex-1">
                  <p className="text-atelier-ink">{p.path}</p>
                  <p className="text-atelier-mute mt-1">
                    {p.exists ? `磁碟剩 ${p.disk_free_mb} MB` : '不存在'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <StatusBadge ok={p.exists} label={p.exists ? 'EXISTS' : 'MISSING'} />
                  <StatusBadge ok={p.writable} label={p.writable ? 'WRITABLE' : 'READ-ONLY'} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Env vars */}
      {status && (
        <div className="atelier-card p-6 space-y-3">
          <p className="atelier-caption">ENVIRONMENT</p>
          <div className="grid grid-cols-2 gap-3 font-mono text-xs">
            {Object.entries(status.env).map(([k, v]) => (
              <div key={k} className="p-2 bg-atelier-surface-2 rounded border border-atelier-line">
                <p className="text-atelier-mute">{k}</p>
                <p className="text-atelier-ink break-all">{v ?? '<unset>'}</p>
              </div>
            ))}
            <div className="p-2 bg-atelier-surface-2 rounded border border-atelier-line">
              <p className="text-atelier-mute">PYTHON</p>
              <p className="text-atelier-ink break-all">{status.python_executable}</p>
            </div>
          </div>
        </div>
      )}

      {/* Last install log */}
      {status?.last_install_log && (
        <div className="atelier-card p-6 space-y-3">
          <div className="flex items-center justify-between">
            <p className="atelier-caption">LAST INSTALL LOG</p>
            {status.last_install_error && (
              <span className="text-xs text-red-400">❌ {status.last_install_error.slice(0, 100)}</span>
            )}
          </div>
          <pre className="text-[11px] leading-relaxed font-mono text-atelier-mute bg-atelier-surface-2 border border-atelier-line p-4 rounded max-h-96 overflow-auto whitespace-pre-wrap">
            {status.last_install_log}
          </pre>
        </div>
      )}

      {/* Install result */}
      {installResult && (
        <div className={`atelier-card p-6 space-y-3 border-l-4 ${installResult.ok ? 'border-green-500' : 'border-red-500'}`}>
          <p className="atelier-caption">
            {installResult.ok ? '✅ INSTALL RESULT · SUCCESS' : '❌ INSTALL RESULT · FAILED'}
          </p>
          {installResult.error && (
            <p className="text-xs text-red-400 font-mono">{installResult.error}</p>
          )}
          {installResult.chromium_executable && (
            <p className="text-xs font-mono text-atelier-mute break-all">
              {installResult.chromium_executable}
            </p>
          )}
          {installResult.log && (
            <pre className="text-[11px] leading-relaxed font-mono text-atelier-mute bg-atelier-surface-2 border border-atelier-line p-4 rounded max-h-96 overflow-auto whitespace-pre-wrap">
              {installResult.log}
            </pre>
          )}
        </div>
      )}

      {/* Help */}
      <div className="atelier-card p-6 space-y-2">
        <p className="atelier-caption flex items-center gap-2">
          <Wrench className="w-3 h-3" /> TROUBLESHOOTING
        </p>
        <ul className="text-xs text-atelier-mute leading-relaxed space-y-1 list-disc list-inside">
          <li>如果按「立即安裝 Chromium」後成功 → 輪播 ZIP / PNG 下載即時可用，唔使 redeploy</li>
          <li>如果出 <code className="text-atelier-coral">timeout</code> → pod outbound 可能被 firewall 擋，聯絡 Emergent support</li>
          <li>如果出 <code className="text-atelier-coral">permission denied</code> → 兩個 candidate path 都 read-only，需要 Emergent 增加 writable mount</li>
          <li>如果出 <code className="text-atelier-coral">no space left</code> → 磁碟唔夠，要清 cache 或加 disk</li>
        </ul>
      </div>
    </div>
  );
}
